import { useSyncExternalStore } from 'react'
import { apiConfig, apiUrl, configureApi, type ApiConfig } from './config'
import type { User } from './types'

export interface SessionState {
  status: 'loading' | 'anonymous' | 'authenticated'
  user: User | null
  token: string | null
  needsSetup: boolean
  openRegistration: boolean
  /** Single sign-on button label, when the server has OIDC configured. */
  sso: string | null
}

type Listener = () => void
const REFRESH_EVERY_MS = 12 * 60_000

let state: SessionState = { status: 'loading', user: null, token: null, needsSetup: false, openRegistration: false, sso: null }
const listeners = new Set<Listener>()
let timer: ReturnType<typeof setInterval> | null = null
let inflight: Promise<boolean> | null = null

function set(next: Partial<SessionState>): void {
  state = { ...state, ...next }
  for (const l of listeners) l()
}

function schedule(): void {
  if (timer) clearInterval(timer)
  timer = setInterval(() => void session.refresh(), REFRESH_EVERY_MS)
}

export const session = {
  get: () => state,
  subscribe(l: Listener): () => void {
    listeners.add(l)
    return () => listeners.delete(l)
  },

  /** The panel calls this once at boot, before anything else touches the API. */
  configure(cfg: Partial<ApiConfig>): void {
    configureApi(cfg)
  },

  /** On page load: find out whether setup is needed, then try the refresh cookie. */
  async bootstrap(): Promise<void> {
    if (apiConfig().tokenStore) return session.restore()
    try {
      const res = await fetch(apiUrl('/api/setup/status'))
      const s = (await res.json()) as { needsSetup: boolean; openRegistration?: boolean; sso?: { label: string } | null }
      set({ openRegistration: Boolean(s.openRegistration), sso: s.sso?.label ?? null })
      if (s.needsSetup) return set({ status: 'anonymous', needsSetup: true })
    } catch { /* offline: fall through to refresh, which fails cleanly */ }
    if (!(await session.refresh())) set({ status: 'anonymous', needsSetup: false })
  },

  /**
   * A token client has no refresh cookie: the stored token *is* the session, so it is validated
   * against the server before the UI trusts it. A token the server no longer accepts is dropped
   * here rather than failing every call afterwards (docs/extension.md §7.4).
   */
  async restore(): Promise<void> {
    const store = apiConfig().tokenStore
    const token = store ? await store.load().catch(() => null) : null
    if (!token) return set({ status: 'anonymous', user: null, token: null })
    set({ token })
    try {
      const res = await fetch(apiUrl('/api/me'), { headers: { authorization: `Bearer ${token}` } })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      set({ status: 'authenticated', user: (await res.json()) as User, token, needsSetup: false })
    } catch {
      await store?.save(null).catch(() => undefined)
      set({ status: 'anonymous', user: null, token: null })
    }
  },

  signedIn(r: { accessToken: string; user: User }): void {
    set({ status: 'authenticated', token: r.accessToken, user: r.user, needsSetup: false })
    void apiConfig().tokenStore?.save(r.accessToken).catch(() => undefined)
    schedule()
  },

  setUser(user: User): void {
    set({ user })
  },

  /** Rotates the refresh cookie for a new access token. Concurrent callers share one request. */
  refresh(): Promise<boolean> {
    inflight ??= (async () => {
      try {
        const res = await fetch(apiUrl('/api/auth/refresh'), { method: 'POST', credentials: 'same-origin' })
        if (!res.ok) {
          if (state.status === 'authenticated') set({ status: 'anonymous', token: null, user: null })
          return false
        }
        session.signedIn((await res.json()) as { accessToken: string; user: User })
        return true
      } catch {
        return false
      } finally {
        inflight = null
      }
    })()
    return inflight
  },

  async logout(): Promise<void> {
    if (timer) clearInterval(timer)
    // A token client has no cookie to clear, and `/api/auth/logout` is not its endpoint.
    if (apiConfig().canRefresh) await fetch(apiUrl('/api/auth/logout'), { method: 'POST', credentials: 'same-origin' }).catch(() => undefined)
    await apiConfig().tokenStore?.save(null).catch(() => undefined)
    set({ status: 'anonymous', token: null, user: null })
  },

  /** The server revoked this session (disabled account, password changed elsewhere). */
  expired(): void {
    if (timer) clearInterval(timer)
    void apiConfig().tokenStore?.save(null).catch(() => undefined)
    set({ status: 'anonymous', token: null, user: null })
  }
}

export function useSession(): SessionState {
  return useSyncExternalStore(session.subscribe, session.get)
}
